import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Input, List, ListItem, Spinner, Tag, useOutsideClick } from '@chakra-ui/react';
import { useGetPlacesQuery } from '../api/api';
import { Place } from '../../types';

const MAX_RESULTS = 50;
// Stored coordinates come from a place in this same list rounded to 4
// decimals, so an exact match is expected; the tolerance only exists to
// still recognize a place across that rounding. ~0.02 degrees is ~2 km.
const MATCH_TOLERANCE_DEG = 0.02;

// findPlace returns the place matching the given coordinates, or undefined
// if none is within MATCH_TOLERANCE_DEG (e.g. a hand-entered/legacy value).
export const findPlace = (places: Place[], lat?: number, lon?: number): Place | undefined => {
	if (!lat || !lon || places.length === 0) {
		return undefined;
	}

	const lonScale = Math.cos((lat * Math.PI) / 180);
	let best: Place | undefined;
	let bestDist = Infinity;
	for (const p of places) {
		const dLat = p.latitude - lat;
		const dLon = (p.longitude - lon) * lonScale;
		const dist = dLat * dLat + dLon * dLon;
		if (dist < bestDist) {
			bestDist = dist;
			best = p;
		}
	}

	return best && Math.sqrt(bestDist) <= MATCH_TOLERANCE_DEG ? best : undefined;
};

// duplicateNames returns the set of place names that occur more than once,
// so those can be disambiguated with their county in the UI.
const duplicateNames = (places: Place[]): Set<string> => {
	const seen = new Set<string>();
	const dupes = new Set<string>();
	for (const p of places) {
		if (seen.has(p.name)) {
			dupes.add(p.name);
		}
		seen.add(p.name);
	}
	return dupes;
};

const placeLabel = (place: Place, dupes: Set<string>): string =>
	dupes.has(place.name) ? `${place.name} (${place.county})` : place.name;

interface PlaceSelectProps {
	latitude?: number;
	longitude?: number;
	onSelect: (place: Place) => void;
	isInvalid?: boolean;
}

export const PlaceSelect = ({ latitude, longitude, onSelect, isInvalid }: PlaceSelectProps) => {
	const { data: places, isFetching } = useGetPlacesQuery();
	const [query, setQuery] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [highlighted, setHighlighted] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);

	const dupes = useMemo(() => duplicateNames(places ?? []), [places]);
	const matched = useMemo(() => findPlace(places ?? [], latitude, longitude), [places, latitude, longitude]);
	const matchedLabel = matched ? placeLabel(matched, dupes) : '';

	// Keep the input text in sync with the externally selected place, but only
	// while the user isn't actively browsing the dropdown.
	useEffect(() => {
		if (!isOpen) {
			setQuery(matchedLabel);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [matchedLabel, isOpen]);

	useOutsideClick({
		ref: containerRef,
		handler: () => {
			setIsOpen(false);
			setQuery(matchedLabel);
		},
	});

	const results = useMemo(() => {
		const needle = query.trim().toLocaleLowerCase('sv');
		if (!places || !needle) {
			return [];
		}
		const starts: Place[] = [];
		const contains: Place[] = [];
		for (const p of places) {
			const label = placeLabel(p, dupes).toLocaleLowerCase('sv');
			if (label.startsWith(needle)) {
				starts.push(p);
			} else if (starts.length + contains.length < MAX_RESULTS && label.includes(needle)) {
				contains.push(p);
			}
			if (starts.length >= MAX_RESULTS) {
				break;
			}
		}
		return [...starts, ...contains].slice(0, MAX_RESULTS);
	}, [places, query, dupes]);

	const selectPlace = (place: Place) => {
		onSelect(place);
		setQuery(placeLabel(place, dupes));
		setIsOpen(false);
	};

	if (isFetching) {
		return <Spinner size="sm" />;
	}

	const showUnmatchedChip = !matched && !!latitude && !!longitude && !isOpen;

	return (
		<Box ref={containerRef} position="relative">
			<Input
				placeholder="Sök ort, t.ex. Växjö"
				value={query}
				isInvalid={isInvalid}
				autoComplete="off"
				role="combobox"
				aria-expanded={isOpen}
				aria-autocomplete="list"
				onFocus={() => setIsOpen(true)}
				onChange={(e) => {
					setQuery(e.target.value);
					setIsOpen(true);
					setHighlighted(0);
				}}
				onKeyDown={(e) => {
					if (e.key === 'ArrowDown') {
						e.preventDefault();
						setIsOpen(true);
						setHighlighted((i) => Math.min(i + 1, results.length - 1));
					} else if (e.key === 'ArrowUp') {
						e.preventDefault();
						setHighlighted((i) => Math.max(i - 1, 0));
					} else if (e.key === 'Enter') {
						e.preventDefault();
						const place = results[highlighted];
						if (place) {
							selectPlace(place);
						}
					} else if (e.key === 'Escape') {
						setIsOpen(false);
						setQuery(matchedLabel);
					}
				}}
			/>
			{showUnmatchedChip && (
				<Tag mt={2} size="sm" colorScheme="gray">
					Egen position ({latitude!.toFixed(2)}, {longitude!.toFixed(2)})
				</Tag>
			)}
			{isOpen && results.length > 0 && (
				<List
					position="absolute"
					zIndex={10}
					mt={1}
					bg="white"
					_dark={{ bg: 'gray.700' }}
					boxShadow="md"
					borderRadius="md"
					border="1px solid"
					borderColor="gray.200"
					maxH="240px"
					overflowY="auto"
					width="100%"
					styleType="none"
					m={0}
					role="listbox"
				>
					{results.map((p, i) => (
						<ListItem
							key={`${p.name}-${p.county}`}
							px={3}
							py={1}
							cursor="pointer"
							role="option"
							aria-selected={i === highlighted}
							bg={i === highlighted ? 'teal.50' : undefined}
							_dark={{ bg: i === highlighted ? 'teal.700' : undefined }}
							onMouseDown={(e) => {
								// fires before the input's blur, so the click isn't lost
								// to the outside-click restore handler above.
								e.preventDefault();
								selectPlace(p);
							}}
							onMouseEnter={() => setHighlighted(i)}
						>
							{placeLabel(p, dupes)}
						</ListItem>
					))}
				</List>
			)}
		</Box>
	);
};
